import {
  Box,
  HStack,
  Text,
  VStack,
} from '@chakra-ui/react';
import React from 'react';
import { sanitizeHtml } from '../../utils/textConversion';

interface FormattedTextProps {
  content: string;
  maxHeight?: string;
  overflow?: string;
  fontSize?: string;
  lineHeight?: string;
  spacing?: string;
}

const FormattedText: React.FC<FormattedTextProps> = ({
  content,
  maxHeight = 'auto',
  overflow = 'visible',
  fontSize = '0.875rem',
  lineHeight = '1.5',
  spacing = '0.5rem',
}) => {
  if (content.includes('<') && content.includes('>')) {
    const sanitizedHtml = sanitizeHtml(content);
    return (
      <Box
        dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
        maxHeight={maxHeight}
        overflow={overflow}
        fontSize={fontSize}
        lineHeight={lineHeight}
        wordBreak="break-word"
        overflowWrap="anywhere"
        minWidth={0}
        sx={{
          p: { mb: spacing, '&:last-child': { mb: 0 } },
          'h1, h2, h3': {
            fontWeight: 'bold',
            mb: spacing,
            mt: spacing,
            '&:first-child': { mt: 0 },
          },
          h1: { fontSize: '1.25rem' },
          h2: { fontSize: '1.125rem' },
          h3: { fontSize: '1rem' },
          'strong, b': { fontWeight: 'bold' },
          'em, i': { fontStyle: 'italic' },
          's, strike': { textDecoration: 'line-through' },
          'ul, ol': {
            mb: spacing,
            pl: '1.5rem',
            '&:last-child': { mb: 0 },
          },
          li: { mb: '0.25rem' },
          blockquote: {
            borderLeft: '3px solid',
            borderColor: 'gray.300',
            pl: '1rem',
            ml: '0.5rem',
            fontStyle: 'italic',
            color: 'gray.600',
            mb: spacing,
          },
          br: {
            display: 'block',
            content: '""',
            marginTop: '0.25rem',
          },
          a: {
            color: 'blue.600',
            textDecoration: 'underline',
            _hover: {
              color: 'blue.700',
              textDecoration: 'underline',
            },
            '&[target="_blank"]': {
              '&::after': {
                content: '" ↗"',
                fontSize: '0.75em',
                marginLeft: '0.125rem',
              },
            },
          },
        }}
      />
    );
  }

  // Handle plain text with formatting markers (legacy support)
  const lines = content.split('\n');

  return (
    <Box maxHeight={maxHeight} overflow={overflow} wordBreak="break-word" overflowWrap="anywhere" minWidth={0}>
      <VStack align="stretch" spacing="0.25rem">
        {lines.map((line, index) => {
          if (line.trim() === '') {
            return <Box key={index} h="0.5rem" />;
          }

          // Handle headers
          if (line.startsWith('# ')) {
            return (
              <Text
                key={index}
                fontSize="1.25rem"
                fontWeight="bold"
                mb="0.5rem"
              >
                {line.substring(2)}
              </Text>
            );
          }
          if (line.startsWith('## ')) {
            return (
              <Text
                key={index}
                fontSize="1.125rem"
                fontWeight="bold"
                mb="0.5rem"
              >
                {line.substring(3)}
              </Text>
            );
          }
          if (line.startsWith('### ')) {
            return (
              <Text
                key={index}
                fontSize="1rem"
                fontWeight="bold"
                mb="0.5rem"
              >
                {line.substring(4)}
              </Text>
            );
          }

          // Handle lists
          if (line.startsWith('• ') || line.startsWith('- ')) {
            return (
              <HStack key={index} align="flex-start" spacing="0.5rem">
                <Box
                  as="span"
                  color="blue.500"
                  fontSize="0.75rem"
                  mt="0.125rem"
                >
                  •
                </Box>
                <Text fontSize={fontSize} lineHeight={lineHeight}>
                  {line.substring(2)}
                </Text>
              </HStack>
            );
          }

          // Handle numbered lists
          if (/^\d+\.\s/.test(line)) {
            const match = line.match(/^(\d+)\.\s(.+)/);
            if (match) {
              return (
                <HStack
                  key={index}
                  align="flex-start"
                  spacing="0.5rem"
                >
                  <Box
                    as="span"
                    color="blue.500"
                    fontSize="0.75rem"
                    fontWeight="bold"
                    mt="0.125rem"
                  >
                    {match[1]}.
                  </Box>
                  <Text fontSize={fontSize} lineHeight={lineHeight}>
                    {match[2]}
                  </Text>
                </HStack>
              );
            }
          }

          // Handle quotes
          if (line.startsWith('> ')) {
            return (
              <Box
                key={index}
                borderLeft="3px solid"
                borderColor="gray.300"
                pl="1rem"
                ml="0.5rem"
                fontStyle="italic"
                color="gray.600"
                mb="0.5rem"
              >
                <Text fontSize={fontSize} lineHeight={lineHeight}>
                  {line.substring(2)}
                </Text>
              </Box>
            );
          }

          // Handle bold text
          let processedLine = line;
          processedLine = processedLine.replace(
            /\*\*(.*?)\*\*/g,
            '<strong>$1</strong>'
          );
          processedLine = processedLine.replace(
            /\*(.*?)\*/g,
            '<em>$1</em>'
          );
          processedLine = processedLine.replace(
            /~~(.*?)~~/g,
            '<s>$1</s>'
          );

          return (
            <Text
              key={index}
              fontSize={fontSize}
              lineHeight={lineHeight}
              dangerouslySetInnerHTML={{ __html: processedLine }}
              sx={{
                strong: { fontWeight: 'bold' },
                em: { fontStyle: 'italic' },
                s: { textDecoration: 'line-through' },
              }}
            />
          );
        })}
      </VStack>
    </Box>
  );
};

export default FormattedText;
